import React, { useEffect, useState, useRef, useCallback, useMemo, useContext } from 'react';
import { useParams, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';

import { UseOutletContextBankStatement } from './BankStatement';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { BankStatementDA } from '../../../../services/admin/bank.statement.service';
import { BankStatement, BankStatementLine, AutoMatchJob, DuplicateCheck } from '../../../../models/bank.statement.model';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney, formatAccountingAccount } from '../../../../scripts/format.value.script';
import { modalShow, modalHide } from '../../../../utils/modal.util';
import { GlobalSwal } from '../../../../styles/swal.style';
import { MediaDA } from '../../../../services/admin/media.service';
import toast from 'react-hot-toast';

import AppPreloader from '../../../../components/Preloader';
import AppPdfViewer from '../../../../components/element/PdfViewer';
import AppAutoTextarea from '../../../../components/element/AutoTextarea';
import AppMoneyInput from '../../../../components/element/MoneyInput';
import AppModalBankLineMatch, { MatchCandidate, UnvalidatedCandidate, PayableCandidate } from '../../../../components/modal/ModalBankLineMatch';
import AppModalProviderInvoiceUpdateValidate from '../../../../components/modal/ModalProviderInvoiceUpdateStatusValidate';
import AppModalExpenseUpdateValidate from '../../../../components/modal/ModalExpenseUpdateStatusValidate';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { usePdfViewer } from '../../../../contexts/PdfViewerContext';
import { sanitizeFilename } from '../../../../scripts/document.filename.script';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { Settings } from '../../../../models/settings.model';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { ExpenseDA } from '../../../../services/admin/expense.service';
import { AccountingAccountDA } from '../../../../services/admin/accounting.account.service';
import { BookingJournalDA } from '../../../../services/admin/booking.journal.service';
import { SettingsDA } from '../../../../services/admin/settings.service';
import { ServiceDA } from '../../../../services/admin/service.service';
import { ProductDA } from '../../../../services/admin/product.service';
import { Invoice } from '../../../../models/invoice.model';
import { Service } from '../../../../models/service.model';
import { Product } from '../../../../models/product.model';
import AppModalInvoiceUpdateValidate from '../../../../components/modal/ModalInvoiceUpdateStatusValidate';
import AppModalManualEntry from '../../../../components/modal/ModalManualEntry';

export interface BankStatementViewPageProps {};

const POLLING_INTERVAL_MS = 4000

const BankStatementViewPage: React.FunctionComponent<BankStatementViewPageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany} = UseOutletContextBankStatement()
  const {lang} = StateLanguage()
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const pdfViewer = usePdfViewer()

  const [mounted, setMounted] = useState(false)
  const [statement, setStatement] = useState<BankStatement | null>(null)
  const [notFound, setNotFound] = useState(false)

  // manual match modal state
  const [matchLine, setMatchLine] = useState<BankStatementLine | null>(null)
  const [matchCandidates, setMatchCandidates] = useState<MatchCandidate[]>([])
  const [matchLoading, setMatchLoading] = useState(false)
  const [paymentSuggestions, setPaymentSuggestions] = useState<{ unvalidated_candidates?: any[]; payable_candidates?: PayableCandidate[] }>({})

  // Validate-and-reconcile state
  const [validationProviderInvoice, setValidationProviderInvoice] = useState<ProviderInvoice | null>(null)
  const [validationInvoice, setValidationInvoice] = useState<Invoice | null>(null)
  const [validationExpense, setValidationExpense] = useState<Expense | null>(null)
  const [validationIncomeAccounts, setValidationIncomeAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationServices, setValidationServices] = useState<Service[] | null>(null)
  const [validationProducts, setValidationProducts] = useState<Product[] | null>(null)
  const [validationAccounts, setValidationAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationTaxAccounts, setValidationTaxAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationExpenseAccounts, setValidationExpenseAccounts] = useState<AccountingAccount[] | null>(null)
  const [validationSettings, setValidationSettings] = useState<Settings | null>(null)
  const [bankReconContext, setBankReconContext] = useState<{ id_bank_statement: string; id_bank_statement_line: string; bank_date: string; bank_amount: number; id_treasury_account: string } | null>(null)

  // Manual entry state
  const [journals, setJournals] = useState<Array<{ id: string; type: string; name: string }>>([])
  const [manualEntryLine, setManualEntryLine] = useState<BankStatementLine | null>(null)

  // Draft mode state
  const [draftLines, setDraftLines] = useState<BankStatementLine[]>([])
  const [draftOpeningBalance, setDraftOpeningBalance] = useState<number>(0)
  const [draftClosingBalance, setDraftClosingBalance] = useState<number>(0)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [statementPdfUrl, setStatementPdfUrl] = useState<string | null>(null)
  const [includedLines, setIncludedLines] = useState<boolean[]>([])

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isPollingRef = useRef(false)

  const loadStatement = useCallback(async (showSpinner: boolean = false) => {
    if (!companyForUser || !id) return

    if (showSpinner) setStatement(null)

    try {
      const response = await BankStatementDA.getStatement(companyForUser.id, id)

      if (response.status === 200 || response.status === 304) {
        const payload = response.data
        if (payload && payload.id) {
          // Transitional: normalize _id → id on lines until backend lot "convention id lignes" is deployed.
          // REMOVE this block once all responses expose `id` on every line.
          if (Array.isArray(payload.lines)) {
            for (const ln of payload.lines) {
              if (!ln.id && ln._id) { ln.id = ln._id }
            }
          }
          setStatement(payload)
          setNotFound(false)
        } else {
          setNotFound(true)
          setStatement(null)
        }
      } else if (response.status === 404) {
        setNotFound(true)
        setStatement(null)
      } else {
        setStatement(null)
      }
    } catch (err) {
      console.error('[BankStatementView] load failed', err)
      setStatement(null)
    }
  }, [companyForUser, id])

  // Initialize draft lines when statement loads in draft mode
  useEffect(() => {
    if (statement?.import_status === 'draft') {
      const lines = statement.lines ?? []
      setDraftLines(lines)
      setDraftOpeningBalance(statement.opening_balance ?? 0)
      setDraftClosingBalance(statement.closing_balance ?? 0)
      setIncludedLines(lines.map((l: any) => !l.is_duplicate))
      setIsDirty(false)
    }
  }, [statement])

  // Retry extraction state
  const [retrying, setRetrying] = useState(false)

  const handleRetryExtraction = async () => {
    if (!companyForUser || !id || retrying) return
    setRetrying(true)
    try {
      const res = await BankStatementDA.retryExtraction(companyForUser.id, id)
      if (res.status === 200 || res.status === 201 || res.status === 202) {
        await loadStatement(false)
      } else {
        const msg = res.data?.message ?? lang.labels.error
        GlobalSwal.fire({ icon: 'error', title: lang.labels.error, text: msg })
        setRetrying(false)
      }
    } catch (err: any) {
      GlobalSwal.fire({ icon: 'error', title: lang.labels.error, text: err?.message ?? lang.labels.error })
      setRetrying(false)
    }
  }

  // Polling logic
  const updatePolling = useCallback((stmt: BankStatement) => {
    const autoMatchActive = stmt.auto_match_job?.status === 'queued' || stmt.auto_match_job?.status === 'running'
    const extractionActive = stmt.extraction_job?.status === 'queued' || stmt.extraction_job?.status === 'running'
    const jobActive = autoMatchActive || extractionActive

    if (jobActive && !isPollingRef.current) {
      isPollingRef.current = true
      pollingRef.current = setInterval(() => {
        loadStatement(false)
      }, POLLING_INTERVAL_MS)
    } else if (!jobActive && isPollingRef.current) {
      isPollingRef.current = false
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [loadStatement])

  useEffect(() => {
    loadStatement(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id])

  useEffect(() => {
    if (statement) {
      updatePolling(statement)
      // Reset retrying flag when extraction job finishes
      const exStatus = statement.extraction_job?.status
      if (retrying && exStatus !== 'queued' && exStatus !== 'running') {
        setRetrying(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement, updatePolling])

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
        isPollingRef.current = false
      }
    }
  }, [])

  // Load signed PDF URL for any statement with a source document
  useEffect(() => {
    const loadPdf = async () => {
      if (statement?.id_document_inbox && companyForUser) {
        const url = await MediaDA.getDocumentSignedUrl(companyForUser.id, statement.id_document_inbox)
        if (url) setStatementPdfUrl(url)
      }
    }
    loadPdf()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement?.id, statement?.import_status])

  // Load journals for manual entry modal
  useEffect(() => {
    if (!companyForUser) return
    BookingJournalDA.getBookingJournals(companyForUser.id, '', '', '').then(res => {
      if (res.status === 200) {
        const data = res.data
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data?.booking_journals) ? data.booking_journals : (Array.isArray(data) ? data : []))
        setJournals(items.map((j: any) => ({ id: j.id ?? j._id, type: j.type ?? '', name: j.name ?? '' })))
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.treasuryAccounts, branch: lang.labels.bankStatements}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Actions ──

  const handleAutoMatch = async () => {
    if (!companyForUser || !id || !statement) return

    const job = statement.auto_match_job
    if (job?.status === 'queued' || job?.status === 'running') return

    try {
      const response = await BankStatementDA.triggerAutoMatch(companyForUser.id, id)
      if (response.status === 200 || response.status === 202) {
        await loadStatement(false)
      }
    } catch (err) {
      console.error('[BankStatementView] auto-match trigger failed', err)
    }
  }

  /** Guard: surface a reload prompt when a line has no usable id. */
  const requireLineId = (line: BankStatementLine): string | null => {
    // Transitional: accept _id while backend lot "convention id lignes" is pending.
    // REMOVE the _id fallback once all responses expose `id`.
    const resolved = line.id || (line as any)._id
    if (!resolved) {
      console.error('[BankStatementView] line has no id — action blocked', line)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    }
    return resolved ?? null
  }

  const handleOpenMatchModal = async (line: BankStatementLine) => {
    if (!companyForUser || !id) return
    if (!requireLineId(line)) return

    setMatchLine(line)
    setMatchCandidates([])
    setPaymentSuggestions({})
    setMatchLoading(true)
    modalShow('modal-bank-line-match')

    const [candidatesRes, paymentsRes] = await Promise.allSettled([
      BankStatementDA.getLineCandidates(companyForUser.id, id, line.id),
      BankStatementDA.getPaymentSuggestions(companyForUser.id, id, line.id),
    ])

    // candidates
    if (candidatesRes.status === 'fulfilled' && candidatesRes.value.status === 200) {
      const data = candidatesRes.value.data
      const items: MatchCandidate[] = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
      setMatchCandidates(items)
    } else {
      setMatchCandidates([])
    }

    // payment suggestions
    if (paymentsRes.status === 'fulfilled' && paymentsRes.value.status === 200) {
      const data = paymentsRes.value.data
      setPaymentSuggestions({
        unvalidated_candidates: Array.isArray(data?.unvalidated_candidates) ? data.unvalidated_candidates : [],
        payable_candidates: Array.isArray(data?.payable_candidates) ? data.payable_candidates : [],
      })
    } else {
      setPaymentSuggestions({})
    }

    setMatchLoading(false)
  }

  const handleMatched = async (lineId: string, idEntry: string) => {
    if (!companyForUser || !id) return

    const response = await BankStatementDA.matchLine(companyForUser.id, id, lineId, idEntry)
    if (response.status === 200) {
      await loadStatement(false)
    }
  }

  const handlePayInvoice = async (doc: PayableCandidate) => {
    if (!companyForUser || !matchLine || !id) return
    const resolvedLineId = requireLineId(matchLine)
    if (!resolvedLineId) return

    try {
      const response = await BankStatementDA.payAndLinkInvoice(companyForUser.id, id, resolvedLineId, {
        id_provider_invoice: doc.kind === 'provider-invoice' ? doc.id : undefined,
        id_invoice: doc.kind === 'invoice' ? doc.id : undefined,
        kind: doc.kind,
        amount: Math.abs(matchLine.amount),
        date: matchLine.date,
      })
      if (response.status === 200 || response.status === 201) {
        setMatchLine(null)
        setPaymentSuggestions({})
        modalHide('modal-bank-line-match')
        await loadStatement(false)
      }
    } catch (err) {
      console.error('[BankStatementView] pay invoice failed', err)
    }
  }

  const handleValidateDocument = async (doc: UnvalidatedCandidate) => {
    if (!companyForUser || !matchLine || !id) return
    const resolvedLineId = requireLineId(matchLine)
    if (!resolvedLineId) return

    // Build bank reconciliation context from current line
    setBankReconContext({
      id_bank_statement: id,
      id_bank_statement_line: resolvedLineId,
      bank_date: matchLine.date,
      bank_amount: Math.abs(matchLine.amount),
      id_treasury_account: statement?.id_treasury_account ?? '',
    })

    modalHide('modal-bank-line-match')

    try {
      // Fetch full document + accounting accounts + settings in parallel
      const isExpense = doc.kind === 'expense'
      const isInvoice = doc.kind === 'invoice'

      const [docRes, allRes, taxRes, expRes, incomeRes, settingsRes, servicesRes, productsRes, vatRegimeRes, taxCodesRes] = await Promise.allSettled([
        isExpense
          ? ExpenseDA.getExpense(companyForUser.id, doc.id)
          : isInvoice
            ? InvoiceDA.getInvoice(companyForUser.id, doc.id)
            : ProviderInvoiceDA.getProviderInvoice(companyForUser.id, doc.id),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], [], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, ['1170', '1171'], [], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], ['4', '6'], []),
        AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], ['3'], []),
        SettingsDA.getSettings(),
        isInvoice ? ServiceDA.getServices(companyForUser.id, '', '', '', '') : Promise.resolve(null),
        isInvoice ? ProductDA.getProducts(companyForUser.id, '', '', '', '') : Promise.resolve(null),
        AccountingAccountDA.getVatRegime(companyForUser.id),
        AccountingAccountDA.getTaxCodes(companyForUser.id),
      ])

      // Parse document
      if (docRes.status === 'fulfilled' && docRes.value.status === 200) {
        const data = docRes.value.data?.data ?? docRes.value.data
        if (isExpense) {
          setValidationExpense(data)
          setValidationProviderInvoice(null)
          setValidationInvoice(null)
        } else if (isInvoice) {
          setValidationInvoice(data)
          setValidationProviderInvoice(null)
          setValidationExpense(null)
        } else {
          setValidationProviderInvoice(data)
          setValidationExpense(null)
          setValidationInvoice(null)
        }
      } else {
        console.error('[BankStatementView] failed to load document for validation')
        return
      }

      // Accounts — extract like Purchase.tsx / Main.tsx: response.data.accounting_accounts
      if (allRes.status === 'fulfilled' && allRes.value.status === 200) {
        setValidationAccounts(allRes.value.data.accounting_accounts ?? [])
      }
      if (taxRes.status === 'fulfilled' && taxRes.value.status === 200) {
        setValidationTaxAccounts(taxRes.value.data.accounting_accounts ?? [])
      }
      if (expRes.status === 'fulfilled' && expRes.value.status === 200) {
        setValidationExpenseAccounts(expRes.value.data.accounting_accounts ?? [])
      }
      if (incomeRes.status === 'fulfilled' && incomeRes.value.status === 200) {
        setValidationIncomeAccounts(incomeRes.value.data.accounting_accounts ?? [])
      }
      if (isInvoice && servicesRes.status === 'fulfilled' && servicesRes.value && servicesRes.value.status === 200) {
        const sPayload = servicesRes.value.data ?? {}
        setValidationServices(Array.isArray(sPayload.items) ? sPayload.items : Array.isArray(sPayload) ? sPayload : Array.isArray(sPayload.services) ? sPayload.services : [])
      }
      if (isInvoice && productsRes.status === 'fulfilled' && productsRes.value && productsRes.value.status === 200) {
        const pPayload = productsRes.value.data ?? {}
        setValidationProducts(Array.isArray(pPayload.items) ? pPayload.items : Array.isArray(pPayload) ? pPayload : Array.isArray(pPayload.products) ? pPayload.products : [])
      }
      if (settingsRes.status === 'fulfilled' && settingsRes.value.status === 200) {
        const s = settingsRes.value.data?.data ?? settingsRes.value.data

        // Merge VAT regime overrides (same logic as Main.tsx loadSettingsForCompany)
        const vatOverrides = (vatRegimeRes.status === 'fulfilled' && vatRegimeRes.value && vatRegimeRes.value.status === 200 && vatRegimeRes.value.data)
          ? {
              subject_tax: vatRegimeRes.value.data.subject_tax,
              tax_method: vatRegimeRes.value.data.tax_method,
              tax_rate: vatRegimeRes.value.data.tax_rate,
              tax_declaration: vatRegimeRes.value.data.tax_declaration,
            }
          : {}
        const taxFees = (taxCodesRes.status === 'fulfilled' && taxCodesRes.value && taxCodesRes.value.status === 200 && Array.isArray(taxCodesRes.value.data))
          ? taxCodesRes.value.data
          : s.tax_fees ?? []

        setValidationSettings({ ...s, ...vatOverrides, tax_fees: taxFees })
      }

      // Open appropriate validation modal
      setTimeout(() => {
        if (isExpense) {
          modalShow('modal-expense-update-validate')
        } else if (isInvoice) {
          modalShow('modal-invoice-update-validate')
        } else {
          modalShow('modal-provider-invoice-update-validate')
        }
      }, 400)
    } catch (err) {
      console.error('[BankStatementView] validate document failed', err)
    }
  }

  const handleReconciled = async () => {
    setValidationProviderInvoice(null)
    setValidationInvoice(null)
    setValidationExpense(null)
    setValidationServices(null)
    setValidationProducts(null)
    setBankReconContext(null)
    await loadStatement(false)
  }

  const handleUnmatch = async (line: BankStatementLine) => {
    if (!companyForUser || !id) return
    if (!requireLineId(line)) return

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmUnmatch,
      text: lang.labels.unmatchWillDeleteEntry,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesUnmatch,
      cancelButtonText: lang.labels.noCancel,
    })

    if (!result.isConfirmed) return

    try {
      const response = await BankStatementDA.unmatchLine(companyForUser.id, id, line.id)
      if (response.status === 200) {
        await loadStatement(false)
      }
    } catch (err) {
      console.error('[BankStatementView] unmatch failed', err)
    }
  }

  const handleIgnore = async (line: BankStatementLine) => {
    if (!companyForUser || !id) return
    if (!requireLineId(line)) return

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmIgnore,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesIgnore,
      cancelButtonText: lang.labels.noCancel,
    })

    if (!result.isConfirmed) return

    try {
      const response = await BankStatementDA.ignoreLine(companyForUser.id, id, line.id)
      if (response.status === 200) {
        await loadStatement(false)
      }
    } catch (err) {
      console.error('[BankStatementView] ignore failed', err)
    }
  }

  // ── Manual entry ──

  const resolveTreasury = (): { id: string; label: string; name: string; currency_code: string } => {
    const ta = treasuryAccountsForCompany?.find(t => t.id === statement?.id_treasury_account)
    const accountingId = (ta as any)?.accounting_account?.id ?? ''
    const accountingLabel = (ta as any)?.accounting_account
      ? formatAccountingAccount((ta as any).accounting_account, companyForUser?.language)
      : (ta?.name ?? '')
    const currencyCode = (ta as any)?.accounting_account?.currency?.code ?? (ta as any)?.currency?.code ?? 'CHF'
    return { id: accountingId, label: accountingLabel, name: ta?.name ?? '', currency_code: currencyCode }
  }

  const handleOpenManualEntry = async (line: BankStatementLine) => {
    if (!companyForUser || !id) return
    if (!requireLineId(line)) return
    setManualEntryLine(line)
    if (!validationAccounts) {
      try {
        const res = await AccountingAccountDA.getAccountingAccounts(companyForUser.id, [], [], [])
        if (res.status === 200) {
          setValidationAccounts(res.data?.accounting_accounts ?? (Array.isArray(res.data) ? res.data : []))
        }
      } catch {}
    }
    modalShow('modal-manual-entry')
  }

  const handleManualEntrySuccess = () => {
    setManualEntryLine(null)
    loadStatement(false)
  }

  const handleRestore = async (line: BankStatementLine) => {
    if (!companyForUser || !id) return
    if (!requireLineId(line)) return

    try {
      const response = await BankStatementDA.restoreLine(companyForUser.id, id, line.id)
      if (response.status === 200) {
        await loadStatement(false)
      }
    } catch (err) {
      console.error('[BankStatementView] restore failed', err)
    }
  }

  // ── Draft mode actions ──

  const updateDraftLine = (index: number, field: keyof BankStatementLine, value: any) => {
    setDraftLines(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
    setIsDirty(true)
  }

  const removeDraftLine = (index: number) => {
    setDraftLines(prev => prev.filter((_, i) => i !== index))
    setIncludedLines(prev => prev.filter((_, i) => i !== index))
    setIsDirty(true)
  }

  const addDraftLine = () => {
    const newLine: BankStatementLine = {
      id: `draft-${Date.now()}`,
      date: new Date().toISOString().slice(0, 10),
      amount: 0,
      description: '',
      reference: null,
      iban_counterpart: null,
      reconciliation_status: 'pending',
      id_entry: null,
      id_invoice: null,
      matched_at: null,
      matched_via: null,
      match_note: null,
    }
    setDraftLines(prev => [...prev, newLine])
    setIncludedLines(prev => [...prev, true])
    setIsDirty(true)
  }

  // ISO / Date → "YYYY-MM-DD" for <input type="date">
  const toDateInputValue = (d: string | Date | null | undefined): string => {
    if (!d) return ''
    const date = new Date(d)
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
  }

  // Duplicate detection helpers (recompute on edit)
  const existingKeys = useMemo(() => new Set(statement?.duplicate_check?.existing_keys ?? []), [statement?.duplicate_check])
  const normalizeDescLocal = (s: string) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
  const lineKey = (l: BankStatementLine) => {
    const d = new Date(l.date)
    const ds = isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
    return `${ds}|${Number(l.amount).toFixed(2)}|${normalizeDescLocal(l.description)}`
  }

  const updateDraftLineWithDupCheck = (index: number, field: keyof BankStatementLine, value: any) => {
    updateDraftLine(index, field, value)
    if (field === 'date' || field === 'amount' || field === 'description') {
      setDraftLines(prev => {
        const line = { ...prev[index], [field]: value }
        const isDup = existingKeys.has(lineKey(line))
        if (line.is_duplicate !== isDup) {
          const updated = [...prev]
          updated[index] = { ...line, is_duplicate: isDup }
          setIncludedLines(inc => { const next = [...inc]; next[index] = !isDup; return next })
          return updated
        }
        return prev
      })
    }
  }

  const duplicateCheck: DuplicateCheck | undefined = statement?.duplicate_check
  const hasOverlap = (duplicateCheck?.overlapping_statements?.length ?? 0) > 0
  const duplicateCount = duplicateCheck?.duplicate_count ?? 0

  const computedGap = (() => {
    const sumAmounts = draftLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
    return draftClosingBalance - (draftOpeningBalance + sumAmounts)
  })()

  const isBalanced = Math.abs(computedGap) < 0.01

  const derivedDates = useMemo(() => {
    const times = draftLines.map(l => new Date(l.date).getTime()).filter(t => !isNaN(t))
    if (times.length === 0) return { start: null, end: null }
    return { start: new Date(Math.min(...times)), end: new Date(Math.max(...times)) }
  }, [draftLines])

  const handleSaveDraft = async () => {
    if (!companyForUser || !id || saving) return

    setSaving(true)
    try {
      const linesToSend = draftLines
        .filter((_, index) => includedLines[index] !== false)
        .map(l => ({
          id: l.id?.startsWith('draft-') ? undefined : l.id,
          date: l.date,
          amount: Number(l.amount),
          description: l.description,
          reference: l.reference || null,
          iban_counterpart: l.iban_counterpart || null,
        }))

      const response = await BankStatementDA.saveDraft(companyForUser.id, id, linesToSend, draftOpeningBalance, draftClosingBalance)
      if (response.status === 200 || response.status === 201) {
        const payload = response.data
        if (payload && payload.id) {
          if (Array.isArray(payload.lines)) {
            for (const ln of payload.lines) {
              if (!ln.id && ln._id) { ln.id = ln._id }
            }
          }
          setStatement(payload)
        }
        toast.success(lang.labels.draftSaved)
      } else if (response.status === 409) {
        toast.error(lang.labels.statementNotDraft)
        await loadStatement(false)
      } else {
        GlobalSwal.fire({ title: lang.labels.error, text: response.data?.message ?? lang.labels.importFailed, icon: 'error' })
      }
    } catch (err: any) {
      GlobalSwal.fire({ title: lang.labels.error, text: err.message, icon: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // Navigation guard — unsaved draft changes
  // Compatible with BrowserRouter (no data router required).
  // Covers: in-app navigation (Link, navigate()) via navigator monkey-patch,
  //         tab close / refresh via beforeunload.
  // NOT covered: browser back/forward (popstate) — would require data router migration.
  const isDraft = statement?.import_status === 'draft'
  const shouldBlock = isDirty && isDraft
  const { navigator } = useContext(UNSAFE_NavigationContext as React.Context<any>)
  const blockRef = useRef(shouldBlock)
  blockRef.current = shouldBlock
  const saveDraftRef = useRef(handleSaveDraft)
  saveDraftRef.current = handleSaveDraft

  // beforeunload — tab close / refresh
  useEffect(() => {
    if (!shouldBlock) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [shouldBlock])

  // Intercept in-app navigation (Link, navigate())
  useEffect(() => {
    if (!shouldBlock) return

    const origPush = navigator.push
    const origReplace = navigator.replace
    let handling = false

    const intercept = (orig: Function) => (...args: any[]) => {
      if (!blockRef.current || handling) { orig.apply(navigator, args); return }
      handling = true

      GlobalSwal.fire({
        title: lang.labels.unsavedChanges,
        text: lang.labels.unsavedChangesBody,
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: lang.labels.saveDraft,
        denyButtonText: lang.labels.quitWithoutSaving,
        cancelButtonText: lang.labels.cancel,
      }).then(async (result) => {
        handling = false
        if (result.isConfirmed) {
          await saveDraftRef.current()
          navigator.push = origPush
          navigator.replace = origReplace
          orig.apply(navigator, args)
        } else if (result.isDenied) {
          navigator.push = origPush
          navigator.replace = origReplace
          orig.apply(navigator, args)
        }
        // cancel → do nothing, stay on page
      })
    }

    navigator.push = intercept(origPush)
    navigator.replace = intercept(origReplace)

    return () => {
      navigator.push = origPush
      navigator.replace = origReplace
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock, navigator])

  const handleConfirmImport = async () => {
    if (!companyForUser || !id) return

    if (!isBalanced) {
      const result = await GlobalSwal.fire({
        title: lang.labels.confirmUnbalancedTitle,
        text: lang.labels.confirmUnbalancedText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.confirmImport,
        cancelButtonText: lang.labels.cancel,
      })
      if (!result.isConfirmed) return
    }

    setConfirming(true)
    try {
      const linesToSend = draftLines
        .filter((_, index) => includedLines[index] !== false)
        .map(l => ({
          id: l.id?.startsWith('draft-') ? undefined : l.id,
          date: l.date,
          amount: Number(l.amount),
          description: l.description,
          reference: l.reference || null,
          iban_counterpart: l.iban_counterpart || null,
        }))

      const response = await BankStatementDA.confirmStatement(companyForUser.id, id, linesToSend, draftOpeningBalance, draftClosingBalance)
      if (response.status === 200 || response.status === 201) {
        await loadStatement(false)
      } else {
        GlobalSwal.fire({ title: lang.labels.error, text: response.data?.message ?? lang.labels.importFailed, icon: 'error' })
      }
    } catch (err: any) {
      GlobalSwal.fire({ title: lang.labels.error, text: err.message, icon: 'error' })
    } finally {
      setConfirming(false)
    }
  }

  // ── Derived data ──

  const treasuryAccount: TreasuryAccount | undefined = treasuryAccountsForCompany?.find(t => t.id === statement?.id_treasury_account)
  const lines: BankStatementLine[] = statement?.lines ?? []
  const pendingLines = lines.filter(l => l.reconciliation_status === 'pending')
  const matchedLines = lines.filter(l => l.reconciliation_status === 'matched')
  const ignoredLines = lines.filter(l => l.reconciliation_status === 'ignored')

  // ── Delete statement ──

  const [deleting, setDeleting] = useState(false)

  const handleDeleteStatement = async () => {
    if (!companyForUser || !id || !statement) return
    const matchedCount = matchedLines.length
    const totalLines = lines.length
    const unreconciled = totalLines - matchedCount

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeleteStatementTitle ?? 'Delete this bank statement?',
      html: `<div class="text-start">
        <p class="mb-2"><strong>${treasuryAccount?.name ?? '—'}</strong>${treasuryAccount?.iban ? ` — ${treasuryAccount.iban}` : ''}</p>
        <p class="mb-2">${lang.labels.period ?? 'Period'}: ${dateFormat(lang.code, statement.period_start)} – ${dateFormat(lang.code, statement.period_end)}</p>
        <p class="text-danger">${(lang.labels.confirmDeleteStatementBody ?? 'This action deletes the statement and its {count} unreconciled transaction(s).').replace('{count}', String(unreconciled))}</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete ?? 'Yes, delete',
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' },
    })

    if (!result.isConfirmed) return

    setDeleting(true)
    try {
      const response = await BankStatementDA.deleteStatement(companyForUser.id, id)
      if (response.status === 200) {
        GlobalSwal.fire({ text: lang.labels.statementDeleted ?? 'Statement deleted successfully.', icon: 'success', timer: 2000, showConfirmButton: false })
        navigate('/main/treasury/bank-statement/list')
      } else if (response.status === 409 && response.data?.code === 'STATEMENT_HAS_MATCHED_LINES') {
        const serverCount = response.data.matched_count ?? matchedCount
        GlobalSwal.fire({
          text: (lang.labels.statementHasMatchedLinesError ?? 'This statement has {count} reconciled transaction(s). Unlink them before deleting.').replace('{count}', String(serverCount)),
          icon: 'error',
        })
        await loadStatement(false)
      } else {
        GlobalSwal.fire({ text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
      }
    } catch (err) {
      console.error('[BankStatementView] delete failed', err)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  // ── Invalidate statement ──

  const [invalidating, setInvalidating] = useState(false)
  const hasMatchedLines = matchedLines.length > 0
  const matchedLinesTooltip = hasMatchedLines ? (lang.labels.cannotDeleteMatchedLinesTooltip ?? '').replace('{count}', String(matchedLines.length)) : ''

  const handleInvalidateStatement = async () => {
    if (!companyForUser || !id || !statement) return
    const totalLines = lines.length

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmInvalidateStatementTitle ?? 'Invalidate this bank statement?',
      html: `<div class="text-start">
        <p class="mb-2"><strong>${treasuryAccount?.name ?? '—'}</strong>${treasuryAccount?.iban ? ` — ${treasuryAccount.iban}` : ''}</p>
        <p class="mb-2">${lang.labels.period ?? 'Period'}: ${dateFormat(lang.code, statement.period_start)} – ${dateFormat(lang.code, statement.period_end)}</p>
        <p>${(lang.labels.confirmInvalidateStatementBody ?? 'The statement will revert to draft for correction. Its {count} transaction(s) will no longer be available for reconciliation. The imported document remains attached.').replace('{count}', String(totalLines))}</p>
      </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.confirmInvalidate ?? 'Yes, invalidate',
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-secondary', cancelButton: 'btn btn-light' },
    })

    if (!result.isConfirmed) return

    setInvalidating(true)
    try {
      const response = await BankStatementDA.invalidateStatement(companyForUser.id, id)
      if (response.status === 200) {
        GlobalSwal.fire({ text: lang.labels.statementInvalidated ?? 'Statement reverted to draft.', icon: 'success', timer: 2000, showConfirmButton: false })
        await loadStatement(false)
      } else if (response.status === 409 && response.data?.code === 'STATEMENT_HAS_MATCHED_LINES') {
        const serverCount = response.data.matched_count ?? matchedLines.length
        GlobalSwal.fire({
          text: (lang.labels.statementHasMatchedLinesError ?? 'This statement has {count} reconciled transaction(s). Unlink them before deleting.').replace('{count}', String(serverCount)),
          icon: 'error',
        })
        await loadStatement(false)
      } else {
        GlobalSwal.fire({ text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
      }
    } catch (err) {
      console.error('[BankStatementView] invalidate failed', err)
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' })
    } finally {
      setInvalidating(false)
    }
  }

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany
        ?
        notFound
        ?
        <div className="card">
          <div className="card-body text-center py-10">
            <p className="text-muted fs-5">{lang.labels.statementNotFound}</p>
            <button className="btn btn-secondary mt-3" onClick={() => navigate('/main/treasury/bank-statement/list')}>{lang.labels.back}</button>
          </div>
        </div>
        :
        statement
        ?
        <>
          { isDraft
            ?
            /* ══════ DRAFT MODE — split layout: table left, PDF right ══════ */
            <>
              {/* Draft banner (full width) */}
              <div className="card border border-warning mb-5">
                <div className="card-body py-4 px-5">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                      <span className="badge badge-light-warning fs-7 px-3 py-2">{lang.labels.draftMode}</span>
                      <span className="fw-bold">{lang.labels.statementToValidate}</span>
                      { statement.extraction_job?.extraction_mode &&
                        <span className="badge badge-light-info">
                          {statement.extraction_job.extraction_mode === 'vision' ? lang.labels.extractedByVision : lang.labels.extractedByText}
                        </span>
                      }
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <span className={`badge fs-7 px-3 py-2 ${isBalanced ? 'badge-light-success' : 'badge-light-danger'}`}>
                        { isBalanced
                          ? lang.labels.balanced
                          : (lang.labels.reconciliationGapWarning ?? '').replace('{gap}', formatMoney(computedGap))
                        }
                      </span>
                      { statement.extraction_job?.reconciliation_gap != null &&
                        <small className="text-muted">
                          Extraction gap: {formatMoney(statement.extraction_job.reconciliation_gap)}
                        </small>
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Overlap warning banner */}
              { hasOverlap &&
                <div className="card border border-danger mb-5">
                  <div className="card-body py-3 px-5">
                    <div className="d-flex align-items-center gap-3">
                      <span className="badge badge-light-danger fs-7 px-3 py-2">⚠</span>
                      <div>
                        <div className="fw-bold">
                          {(lang.labels.overlapWarning ?? 'This period overlaps {n} already imported statement(s) for this account.').replace('{n}', String(duplicateCheck?.overlapping_statements?.length ?? 0))}
                        </div>
                        { duplicateCount > 0 &&
                          <div className="text-muted small">
                            {(lang.labels.duplicatesUnchecked ?? '{x} already present transaction(s) unchecked automatically.').replace('{x}', String(duplicateCount))}
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }

              {/* Extraction retry banner */}
              { statement.format === 'PDF' && (() => {
                const exJob = statement.extraction_job
                const exStatus = exJob?.status
                const linesEmpty = !statement.lines || statement.lines.length === 0
                const showFailed = exStatus === 'failed'
                const showEmpty = exStatus === 'completed' && linesEmpty
                const showRunning = exStatus === 'queued' || exStatus === 'running'

                if (showFailed || showEmpty || showRunning) {
                  return (
                    <div className={`card border mb-5 ${showFailed ? 'border-danger' : showRunning ? 'border-info' : 'border-warning'}`}>
                      <div className="card-body py-3 px-5">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-3">
                            { showRunning
                              ? <>
                                  <span className="spinner-border spinner-border-sm text-info"></span>
                                  <span className="fw-bold">{lang.labels.extractionInProgress}</span>
                                </>
                              : showFailed
                              ? <>
                                  <span className="badge badge-light-danger fs-7 px-3 py-2">✗</span>
                                  <span className="fw-bold">{lang.labels.extractionFailedRetry}</span>
                                </>
                              : <>
                                  <span className="badge badge-light-warning fs-7 px-3 py-2">⚠</span>
                                  <span className="fw-bold">{lang.labels.extractionEmptyRetry}</span>
                                </>
                            }
                            { showFailed && exJob?.error &&
                              <small className="text-muted">{exJob.error}</small>
                            }
                          </div>
                          { !showRunning &&
                            <button
                              className="btn btn-sm btn-light-primary"
                              onClick={handleRetryExtraction}
                              disabled={retrying}
                              data-kt-indicator={retrying ? 'on' : 'off'}
                            >
                              <span className="indicator-label">{lang.labels.retryExtraction}</span>
                              <span className="indicator-progress">
                                {lang.labels.retrying}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                              </span>
                            </button>
                          }
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              })()}

              {/* Header card (full width) */}
              <div className="card mb-5">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start flex-wrap mb-5">
                    <div>
                      <h3 className="fw-bolder mb-1">
                        {treasuryAccount?.name ?? '—'}
                        { treasuryAccount?.iban && <small className="text-muted ms-2 fw-normal">{treasuryAccount.iban}</small> }
                      </h3>
                      <div className="d-flex flex-wrap text-muted gap-4 mt-2">
                        { derivedDates.start && derivedDates.end
                          ? <span>{lang.labels.period}: <strong>{dateFormat(lang.code, derivedDates.start)} – {dateFormat(lang.code, derivedDates.end)}</strong></span>
                          : <span>{lang.labels.period}: <strong>—</strong></span>
                        }
                        <span>{lang.labels.format}: <strong>{statement.format}</strong></span>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/main/treasury/bank-statement/list')}>
                        {lang.labels.back}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={handleDeleteStatement}
                        disabled={deleting}
                      >
                        { deleting
                          ? <><span className="spinner-border spinner-border-sm me-1"></span>{lang.labels.deleteStatement}</>
                          : lang.labels.deleteStatement
                        }
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveDraft}
                        disabled={saving || !isDirty}
                        data-kt-indicator={saving ? 'on' : 'off'}
                      >
                        <span className="indicator-label">{lang.labels.saveDraft}</span>
                        <span className="indicator-progress">
                          {lang.labels.savingDraft}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleConfirmImport}
                        disabled={confirming}
                        data-kt-indicator={confirming ? 'on' : 'off'}
                      >
                        <span className="indicator-label">{lang.labels.confirmImport}</span>
                        <span className="indicator-progress">
                          {lang.labels.confirmImport}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="row g-4">
                    <div className="col-md-3">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.openingBalance}</div>
                        <AppMoneyInput
                          className="form-control text-center fw-bolder fs-4 mt-1"
                          value={draftOpeningBalance}
                          onChange={(v) => { setDraftOpeningBalance(v); setIsDirty(true) }}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.closingBalance}</div>
                        <AppMoneyInput
                          className="form-control text-center fw-bolder fs-4 mt-1"
                          value={draftClosingBalance}
                          onChange={(v) => { setDraftClosingBalance(v); setIsDirty(true) }}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.lines ?? 'Lines'}</div>
                        <div className="fw-bolder fs-4">{draftLines.length}</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className={`rounded p-3 text-center ${isBalanced ? 'bg-light-success' : 'bg-light-danger'}`}>
                        <div className="text-muted small">{lang.labels.difference}</div>
                        <div className={`fw-bolder fs-4 ${isBalanced ? 'text-success' : 'text-danger'}`}>{formatMoney(computedGap)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Split: editable table LEFT, PDF RIGHT */}
              <div className="row g-4">
                <div className="col-12 col-xl-6">
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="dataTables_wrapper border border-2 rounded">
                        <div className="table-responsive rounded">
                          <table className="table align-middle table-row-dashed mb-2" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className="bg-light border-bottom-2">
                              <tr className="text-dark text-uppercase fw-bolder fs-7">
                                <th style={{ width: '36px' }}></th>
                                <th style={{ width: '110px' }}>{lang.labels.date}</th>
                                <th>{lang.labels.description}</th>
                                <th style={{ width: '120px' }}>{lang.labels.reference}</th>
                                <th style={{ width: '130px' }}>{lang.labels.counterpartIban ?? 'IBAN'}</th>
                                <th style={{ width: '110px' }} className="text-end">{lang.labels.amount}</th>
                                <th style={{ width: '44px' }}></th>
                              </tr>
                            </thead>
                            <tbody className="text-secondary fw-bold">
                              { draftLines.map((line, index) => {
                                const isIncluded = includedLines[index] !== false
                                return (
                                <tr key={line.id ?? index} style={!isIncluded ? { opacity: 0.45 } : undefined}>
                                  <td className="py-1" style={{ width: '36px' }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isIncluded}
                                      onChange={(e) => {
                                        const next = [...includedLines]; next[index] = e.target.checked; setIncludedLines(next)
                                      }}
                                    />
                                  </td>
                                  <td className="py-1" style={{ width: '110px' }}>
                                    <input
                                      type="date"
                                      className="form-control form-control-sm"
                                      value={toDateInputValue(line.date)}
                                      onChange={(e) => updateDraftLineWithDupCheck(index, 'date', e.target.value)}
                                    />
                                  </td>
                                  <td className="py-1">
                                    <AppAutoTextarea
                                      className="form-control form-control-sm"
                                      value={line.description ?? ''}
                                      onChange={(v) => updateDraftLineWithDupCheck(index, 'description', v)}
                                      minHeight={32}
                                      maxHeight={120}
                                    />
                                    { line.is_duplicate &&
                                      <span className="badge badge-light-warning mt-1" title={line.duplicate_of ?? ''}>
                                        {lang.labels.alreadyImported ?? 'Already imported'}
                                      </span>
                                    }
                                  </td>
                                  <td className="py-1" style={{ width: '120px' }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={line.reference ?? ''}
                                      onChange={(e) => updateDraftLine(index, 'reference', e.target.value)}
                                    />
                                  </td>
                                  <td className="py-1" style={{ width: '130px' }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm font-monospace"
                                      value={line.iban_counterpart ?? ''}
                                      onChange={(e) => updateDraftLine(index, 'iban_counterpart', e.target.value)}
                                    />
                                  </td>
                                  <td className="py-1" style={{ width: '110px' }}>
                                    <AppMoneyInput
                                      className="form-control form-control-sm text-end fw-bold"
                                      value={Number(line.amount) || 0}
                                      onChange={(v) => updateDraftLineWithDupCheck(index, 'amount', v)}
                                      colorBySign
                                    />
                                  </td>
                                  <td className="py-1 text-center" style={{ width: '44px' }}>
                                    <button
                                      className="btn btn-icon btn-sm btn-light-danger"
                                      onClick={() => removeDraftLine(index)}
                                      title={lang.labels.removeLine}
                                    >
                                      <span className="svg-icon svg-icon-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="currentColor" />
                                          <path opacity="0.3" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="currentColor" />
                                          <path opacity="0.3" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="currentColor" />
                                        </svg>
                                      </span>
                                    </button>
                                  </td>
                                </tr>
                                )
                              })}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={7} className="text-center py-3">
                                  <button className="btn btn-sm btn-light-primary" onClick={addDraftLine}>
                                    + {lang.labels.addLine}
                                  </button>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PDF viewer column */}
                <div className="col-12 col-xl-6">
                  <div style={{ height: '80vh', position: 'sticky', top: '1rem' }}>
                    { statementPdfUrl
                      ? <AppPdfViewer pdf={statementPdfUrl} />
                      : <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                          <div className="spinner-border spinner-border-sm me-2"></div>
                          {lang.labels.loading}
                        </div>
                    }
                  </div>
                </div>
              </div>
            </>
            :
            /* ══════ CONFIRMED MODE — unchanged ══════ */
            <>
              {/* Header card */}
              <div className="card mb-5">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start flex-wrap mb-5">
                    <div>
                      <h3 className="fw-bolder mb-1">
                        {treasuryAccount?.name ?? '—'}
                        { treasuryAccount?.iban && <small className="text-muted ms-2 fw-normal">{treasuryAccount.iban}</small> }
                      </h3>
                      <div className="d-flex flex-wrap text-muted gap-4 mt-2">
                        <span>{lang.labels.statementDate}: <strong>{dateFormat(lang.code, statement.statement_date)}</strong></span>
                        <span>{lang.labels.period}: <strong>{dateFormat(lang.code, statement.period_start)} – {dateFormat(lang.code, statement.period_end)}</strong></span>
                        <span>{lang.labels.format}: <strong>{statement.format}</strong></span>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/main/treasury/bank-statement/list')}>
                        {lang.labels.back}
                      </button>
                      { statement.id_document_inbox && statement.format === 'PDF' && statementPdfUrl &&
                        <button
                          className="btn btn-light-info btn-sm"
                          onClick={() => pdfViewer.openUrl(statementPdfUrl, `${sanitizeFilename(treasuryAccount?.name ?? 'statement')}-original.pdf`)}
                        >
                          {lang.labels.viewOriginalPdf}
                        </button>
                      }
                      <button
                        className="btn btn-light btn-sm"
                        onClick={handleInvalidateStatement}
                        disabled={hasMatchedLines || invalidating}
                        title={hasMatchedLines ? matchedLinesTooltip : (lang.labels.invalidateStatement ?? 'Invalidate')}
                      >
                        { invalidating
                          ? <><span className="spinner-border spinner-border-sm me-1"></span>{lang.labels.invalidateStatement}</>
                          : lang.labels.invalidateStatement
                        }
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={handleDeleteStatement}
                        disabled={hasMatchedLines || deleting}
                        title={hasMatchedLines ? matchedLinesTooltip : (lang.labels.deleteStatement ?? 'Delete statement')}
                      >
                        { deleting
                          ? <><span className="spinner-border spinner-border-sm me-1"></span>{lang.labels.deleteStatement}</>
                          : lang.labels.deleteStatement
                        }
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAutoMatch}
                        disabled={statement.auto_match_job?.status === 'queued' || statement.auto_match_job?.status === 'running'}
                      >
                        { (statement.auto_match_job?.status === 'queued' || statement.auto_match_job?.status === 'running')
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>{lang.labels.jobRunning}</>
                          : lang.labels.relaunchAutoMatch
                        }
                      </button>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="row g-4">
                    <div className="col-md-3">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.openingBalance}</div>
                        <div className="fw-bolder fs-4">{formatMoney(statement.opening_balance)}</div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.closingBalance}</div>
                        <div className="fw-bolder fs-4">{formatMoney(statement.closing_balance)}</div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="bg-light-success rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.matched}</div>
                        <div className="fw-bolder fs-4 text-success">{matchedLines.length}</div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="bg-light-warning rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.pending}</div>
                        <div className="fw-bolder fs-4 text-warning">{pendingLines.length}</div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="bg-light rounded p-3 text-center">
                        <div className="text-muted small">{lang.labels.ignored}</div>
                        <div className="fw-bolder fs-4">{ignoredLines.length}</div>
                      </div>
                    </div>
                  </div>

                  {/* Job progress bar */}
                  <JobStatusBar job={statement.auto_match_job} lang={lang} />
                </div>
              </div>

              {/* Confirmed lines table */}
              <div className="card">
                <div className="card-body p-0">
                  <div className="dataTables_wrapper border border-2 rounded">
                    <div className="table-responsive rounded">
                      <table className="table align-middle table-row-bordered gx-10 mb-2">
                        <thead className="bg-light border-bottom-2">
                          <tr className="text-dark text-uppercase fw-bolder fs-7">
                            <th className="min-w-100px">{lang.labels.date}</th>
                            <th className="min-w-200px">{lang.labels.description}</th>
                            <th className="min-w-120px">{lang.labels.reference}</th>
                            <th className="min-w-100px text-end">{lang.labels.amount}</th>
                            <th className="min-w-100px text-center">{lang.labels.status}</th>
                            <th className="min-w-150px text-end">{lang.labels.actions}</th>
                          </tr>
                        </thead>
                        { lines.length > 0
                          ?
                          <tbody className="text-secondary fw-bold">
                            { lines.map((line, index) => (
                              <tr key={index} className="row-dashed">
                                <td className="text-nowrap">{dateFormat(lang.code, line.date)}</td>
                                <td>
                                  <div>{line.description || '—'}</div>
                                  { line.iban_counterpart && <small className="text-muted font-monospace">{line.iban_counterpart}</small> }
                                </td>
                                <td className="font-monospace">{line.reference || '—'}</td>
                                <td className={`text-end fw-bold ${line.amount < 0 ? 'text-danger' : 'text-success'}`}>{formatMoney(line.amount)}</td>
                                <td className="text-center">
                                  <StatusBadge status={line.reconciliation_status} matchedVia={line.matched_via} lang={lang} />
                                </td>
                                <td className="text-end">
                                  <LineActions
                                    line={line}
                                    onMatch={() => handleOpenMatchModal(line)}
                                    onManualEntry={() => handleOpenManualEntry(line)}
                                    onUnmatch={() => handleUnmatch(line)}
                                    onIgnore={() => handleIgnore(line)}
                                    onRestore={() => handleRestore(line)}
                                    lang={lang}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          :
                          <tbody>
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-5">{lang.labels.noLinesInStatement}</td>
                            </tr>
                          </tbody>
                        }
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          }

          <AppModalBankLineMatch
            id_company={companyForUser.id}
            id_statement={id!}
            line={matchLine}
            candidates={matchCandidates}
            loading={matchLoading}
            onMatched={handleMatched}
            paymentSuggestions={paymentSuggestions}
            onValidateDocument={handleValidateDocument}
            onPayInvoice={handlePayInvoice}
            onManualEntry={matchLine ? () => handleOpenManualEntry(matchLine) : undefined}
          />

          <AppModalProviderInvoiceUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingTaxAccountsForCompany={validationTaxAccounts}
            accountingExpenseAccountsForCompany={validationExpenseAccounts}
            providerInvoice={validationProviderInvoice}
            setProviderInvoice={setValidationProviderInvoice}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalInvoiceUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingIncomeAccountsForCompany={validationIncomeAccounts}
            servicesForCompany={validationServices}
            productsForCompany={validationProducts}
            invoice={validationInvoice}
            setInvoice={setValidationInvoice}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalExpenseUpdateValidate
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            accountingAccountsForCompany={validationAccounts}
            accountingTaxAccountsForCompany={validationTaxAccounts}
            accountingExpenseAccountsForCompany={validationExpenseAccounts}
            expense={validationExpense}
            setExpense={setValidationExpense}
            bankReconContext={bankReconContext}
            onReconciled={handleReconciled}
          />

          <AppModalManualEntry
            id_company={companyForUser.id}
            bankLine={{
              line_id: manualEntryLine?.id ?? '',
              id_bank_statement: id ?? '',
              amount: manualEntryLine?.amount ?? 0,
              date: manualEntryLine?.date ?? '',
              description: manualEntryLine?.description ?? '',
              currency_code: resolveTreasury().currency_code,
            }}
            treasuryAccountId={resolveTreasury().id}
            treasuryAccountLabel={resolveTreasury().label}
            treasuryName={resolveTreasury().name}
            accounts={validationAccounts ?? []}
            journals={journals}
            companyForUser={companyForUser}
            settingsForCompany={validationSettings}
            onSuccess={handleManualEntrySuccess}
            onSwitchToMatch={manualEntryLine ? () => handleOpenMatchModal(manualEntryLine) : undefined}
          />
        </>
        :
        <div className="text-center py-10">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{lang.labels.loading}</span>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

// ── Sub-components ──

interface JobStatusBarProps { job: AutoMatchJob; lang: any }

const JobStatusBar: React.FunctionComponent<JobStatusBarProps> = ({job, lang}) => {
  if (!job || job.status === 'idle' || job.status === 'completed') return null

  if (job.status === 'queued') {
    return (
      <div className="d-flex align-items-center mt-5 p-3 bg-light-primary rounded">
        <span className="spinner-border spinner-border-sm text-primary me-3"></span>
        <span>{lang.labels.jobQueued}</span>
      </div>
    )
  }

  if (job.status === 'running') {
    const percent = job.total_lines > 0 ? Math.round((job.processed_lines / job.total_lines) * 100) : 0

    return (
      <div className="mt-5 p-3 bg-light-primary rounded">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <span className="spinner-border spinner-border-sm text-primary me-3"></span>
            <span>{lang.labels.jobRunning}</span>
          </div>
          <span className="fw-bold">{lang.labels.linesProcessed}: {job.processed_lines} / {job.total_lines}</span>
        </div>
        <div className="progress" style={{height: '6px'}}>
          <div className="progress-bar bg-primary progress-bar-striped progress-bar-animated" style={{width: `${percent}%`}}></div>
        </div>
      </div>
    )
  }

  if (job.status === 'failed') {
    return (
      <div className="d-flex align-items-center mt-5 p-3 bg-light-danger rounded">
        <span className="badge badge-light-danger me-3">{lang.labels.jobFailed}</span>
        <span className="text-muted">{job.error}</span>
      </div>
    )
  }

  return null
}

interface StatusBadgeProps { status: string; matchedVia: string | null; lang: any }

const StatusBadge: React.FunctionComponent<StatusBadgeProps> = ({status, matchedVia, lang}) => {
  if (status === 'matched') {
    return (
      <div>
        <span className="badge badge-light-success">{lang.labels.matched}</span>
        { matchedVia && <div className="small text-muted mt-1">{matchedVia}</div> }
      </div>
    )
  }
  if (status === 'ignored') {
    return <span className="badge badge-light-secondary">{lang.labels.ignored}</span>
  }
  return <span className="badge badge-light-warning">{lang.labels.pending}</span>
}

interface LineActionsProps {
  line: BankStatementLine
  onMatch: () => void
  onManualEntry: () => void
  onUnmatch: () => void
  onIgnore: () => void
  onRestore: () => void
  lang: any
}

const LineActions: React.FunctionComponent<LineActionsProps> = ({line, onMatch, onManualEntry, onUnmatch, onIgnore, onRestore, lang}) => {
  if (line.reconciliation_status === 'pending') {
    return (
      <div className="d-flex gap-1 justify-content-end">
        <button className="btn btn-sm btn-primary" onClick={onMatch}>{lang.labels.match}</button>
        <button className="btn btn-sm btn-light-info" onClick={onManualEntry}>{lang.labels.manualEntry ?? 'Manual entry'}</button>
        <button className="btn btn-sm btn-light-secondary" onClick={onIgnore}>{lang.labels.ignore}</button>
      </div>
    )
  }

  if (line.reconciliation_status === 'matched') {
    return (
      <button className="btn btn-sm btn-light-danger" onClick={onUnmatch}>{lang.labels.unmatch}</button>
    )
  }

  if (line.reconciliation_status === 'ignored') {
    return (
      <button className="btn btn-sm btn-light-warning" onClick={onRestore}>{lang.labels.restoreLine}</button>
    )
  }

  return <span className="text-muted">—</span>
}

export default BankStatementViewPage;
